import React, { useCallback, useEffect, useRef, useState } from "react";
import moment from "moment";
import fileDownload from "js-file-download";
import { useLocation } from "react-router-dom";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  FormGroup,
  Input,
  Label,
  Row,
  Table,
} from "reactstrap";
import { ClipLoader } from "react-spinners";
import api from "services/api";
import { useCompanies } from "hooks/companies";
import { useToast } from "../../../../hooks/toast";
import SpecificEmpresaRequiredNotice from "../../../../components/SpecificEmpresaRequiredNotice";
import CustomTable from "../../../../components/CustomTable";
import {
  AUTOPILOT_COLORS,
  autopilotBodyStyle,
  autopilotCardStyle,
  autopilotDescriptionStyle,
  autopilotEyebrowStyle,
  autopilotHeaderStyle,
  autopilotTitleStyle,
} from "../../../../components/autopilot/styles";
import "./styles.css";

const createDefaultFilters = () => ({
  desde: moment().startOf("month").format("YYYY-MM-DD"),
  ate: moment().format("YYYY-MM-DD"),
});

// PO1-3628: os cards da aba Cotação da Torre abrem o Savings com
// ?desde=&ate= para a página mostrar o MESMO período (e o mesmo número).
const REGEX_DATA = /^\d{4}-\d{2}-\d{2}$/;
const filtrosIniciais = (search) => {
  const q = new URLSearchParams(search || "");
  const desde = q.get("desde");
  const ate = q.get("ate");
  if (desde && ate && REGEX_DATA.test(desde) && REGEX_DATA.test(ate) && desde <= ate) return { desde, ate };
  return createDefaultFilters();
};

const QUICK_RANGES = [
  { key: "month", label: "Mês atual", days: null },
  { key: "30d", label: "30 dias", days: 29 },
  { key: "90d", label: "90 dias", days: 89 },
  { key: "180d", label: "180 dias", days: 179 },
];

const getQuickRangeDates = (rangeKey) => {
  const today = moment();
  if (rangeKey === "month") {
    return {
      desde: today.clone().startOf("month").format("YYYY-MM-DD"),
      ate: today.format("YYYY-MM-DD"),
    };
  }
  const range = QUICK_RANGES.find((item) => item.key === rangeKey);
  const days = range && range.days != null ? range.days : 29;
  return {
    desde: today.clone().subtract(days, "days").format("YYYY-MM-DD"),
    ate: today.format("YYYY-MM-DD"),
  };
};

const fmtPct = (v) =>
  v === null || v === undefined
    ? "-"
    : `${Number(v).toLocaleString("pt-BR", {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1,
      })}%`;

const fmtPctSigned = (v) => {
  if (v === null || v === undefined) return "-";
  const num = Number(v);
  return `${num > 0 ? "+" : ""}${num.toLocaleString("pt-BR", {
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  })}%`;
};

const fmtInt = (v) => Number(v || 0).toLocaleString("pt-BR");

const fmtBRL = (v) =>
  Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
  });

const fmtData = (v) => (v ? moment(String(v).slice(0, 10)).format("DD/MM/YYYY") : "-");

const fmtMes = (v) => {
  const m = moment(String(v), ["YYYY-MM", "YYYY-MM-DD"], true);
  return m.isValid() ? m.format("MM/YYYY") : String(v);
};

// Cor de status por tom. Vai para a CSS var --sav-status-color, consumida via
// color-mix() no styles.css (borda, label e ícone), espelhando o padrão de
// status-card do indicador Piso ANTT / Auditoria de Faturas.
const TONE_COLOR = {
  success: "#00bf9f",
  danger: "#fd5d93",
  warning: "#ffb236",
  info: "#1d8cf8",
};

const StatusCard = ({ label, value, helper, iconClassName, tone }) => (
  <div
    className="sav-status-card"
    style={{ "--sav-status-color": TONE_COLOR[tone] || TONE_COLOR.info }}
  >
    <div className="sav-status-card__top">
      <span className="sav-status-card__label">{label}</span>
      {iconClassName ? (
        <span className="sav-status-card__icon">
          <i className={iconClassName} />
        </span>
      ) : null}
    </div>
    <div className="sav-status-card__value">{value}</div>
    {helper ? <div className="sav-status-card__helper">{helper}</div> : null}
  </div>
);

const SavingsGoBID = () => {
  const { empresa, hasEmpresaSelection } = useCompanies();
  const { addToast } = useToast();
  const location = useLocation();
  const empresaId = empresa && empresa.value;

  const [filtros, setFiltros] = useState(() => filtrosIniciais(location.search));
  const filtrosRef = useRef(filtros);
  const [data, setData] = useState(null);
  const [embarques, setEmbarques] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState(null);
  const [consultado, setConsultado] = useState(false);
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [transportadoraFiltro, setTransportadoraFiltro] = useState(null);
  const [drilldownLoading, setDrilldownLoading] = useState(false);

  useEffect(() => {
    filtrosRef.current = filtros;
  }, [filtros]);

  const fetchDrilldown = useCallback(
    async (activeFilters, transportadora) => {
      const params = {
        idEmbarcador: empresaId,
        desde: activeFilters.desde,
        ate: activeFilters.ate,
        limit: 2000,
      };
      if (transportadora) params.transportadora = transportadora;
      const res = await api.get("/indicadores/gobid/savings/drilldown", {
        params,
      });
      return (res.data && res.data.embarques) || [];
    },
    [empresaId]
  );

  const loadDashboard = useCallback(
    async (activeFilters) => {
      if (!empresaId || !hasEmpresaSelection) return;
      if (!activeFilters.desde || !activeFilters.ate) {
        setError('Informe o período ("De" e "Até").');
        return;
      }

      setLoading(true);
      setError(null);
      setTransportadoraFiltro(null);
      try {
        const baseParams = {
          idEmbarcador: empresaId,
          desde: activeFilters.desde,
          ate: activeFilters.ate,
        };
        const [cardsRes, drill] = await Promise.all([
          api.get("/indicadores/gobid/savings/cards", { params: baseParams }),
          fetchDrilldown(activeFilters, null),
        ]);
        setData(cardsRes.data);
        setEmbarques(drill);
        setConsultado(true);
      } catch (requestError) {
        const msg =
          (requestError.response &&
            requestError.response.data &&
            requestError.response.data.error) ||
          "Erro ao carregar o dashboard de Savings.";
        setError(msg);
        setConsultado(true);
        addToast({
          place: "tr",
          type: "danger",
          title: "Savings GoBID",
          description: msg,
          icon: "tim-icons icon-alert-circle-exc",
        });
      } finally {
        setLoading(false);
      }
    },
    [empresaId, hasEmpresaSelection, addToast, fetchDrilldown]
  );

  // Clique no ranking: filtra (ou limpa) os detalhes por transportadora,
  // sem recarregar os cards do período.
  const toggleTransportadora = async (nome) => {
    if (!nome) return;
    const alvo = transportadoraFiltro === nome ? null : nome;
    setTransportadoraFiltro(alvo);
    setDrilldownLoading(true);
    try {
      const drill = await fetchDrilldown(filtrosRef.current, alvo);
      setEmbarques(drill);
    } catch (requestError) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Savings GoBID",
        description: "Falha ao filtrar os detalhes por transportadora.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setDrilldownLoading(false);
    }
  };

  useEffect(() => {
    if (!hasEmpresaSelection) {
      setData(null);
      setEmbarques([]);
      setError(null);
      setConsultado(false);
      return;
    }
    loadDashboard(filtrosRef.current);
  }, [empresaId, hasEmpresaSelection, loadDashboard]);

  const applyQuickRange = (key) => {
    const next = getQuickRangeDates(key);
    setFiltros(next);
    loadDashboard(next);
  };

  const handleExport = async () => {
    if (!empresaId) return;
    setExportLoading(true);
    try {
      const params = {
        idEmbarcador: empresaId,
        desde: filtros.desde,
        ate: filtros.ate,
      };
      if (transportadoraFiltro) params.transportadora = transportadoraFiltro;
      const res = await api.get("/indicadores/gobid/savings/drilldown/export", {
        params,
        responseType: "blob",
      });
      fileDownload(res.data, `gobid-savings-${filtros.desde}-a-${filtros.ate}.xlsx`);
    } catch (requestError) {
      addToast({
        place: "tr",
        type: "danger",
        title: "Exportar XLSX",
        description: "Falha ao gerar a planilha.",
        icon: "tim-icons icon-alert-circle-exc",
      });
    } finally {
      setExportLoading(false);
    }
  };

  if (!hasEmpresaSelection) {
    return (
      <div className="content">
        <SpecificEmpresaRequiredNotice
          title="Selecione um embarcador no cabeçalho"
          message="Este dashboard usa o embarcador selecionado no topo para calcular o savings das cotações GoBID."
        />
      </div>
    );
  }

  const cards = data && data.cards;
  const hasResults = consultado && data && data.totalEmbarques > 0 && cards;
  const isEmpty = consultado && data && data.totalEmbarques === 0;
  const variacao =
    data && data.variacaoVsPeriodoAnterior != null
      ? Number(data.variacaoVsPeriodoAnterior)
      : null;
  const variacaoTone =
    variacao == null ? "info" : variacao >= 0 ? "success" : "danger";
  const topTransportadoras =
    (cards &&
      cards.topTransportadoras &&
      cards.topTransportadoras.itens) ||
    [];
  const tendenciaSerie =
    (cards && cards.tendencia && cards.tendencia.serie) || [];
  const drilldownColumns = [
    { dataField: "numEmbarque", text: "Nº embarque", formatter: (c) => (c != null ? c : "-") },
    { dataField: "dataEmbarque", text: "Data", formatter: (c) => fmtData(c) },
    { dataField: "transportadora", text: "Transportadora", formatter: (c) => c || "-" },
    { dataField: "baseline", text: "Baseline", align: "right", headerAlign: "right", formatter: (c) => (c != null ? fmtBRL(c) : "-") },
    { dataField: "valorContratado", text: "Contratado", align: "right", headerAlign: "right", formatter: (c) => (c != null ? fmtBRL(c) : "-") },
    { dataField: "economia", text: "Economia", align: "right", headerAlign: "right", formatter: (c) => (c != null ? fmtBRL(c) : "-") },
    { dataField: "percentualEconomia", text: "%", align: "right", headerAlign: "right", formatter: (c) => fmtPct(c) },
    { dataField: "valorNF", text: "Valor NF", align: "right", headerAlign: "right", formatter: (c) => (c != null ? fmtBRL(c) : "-") },
    { dataField: "qtdCotacoes", text: "Qtd cotações", align: "right", headerAlign: "right", formatter: (c) => fmtInt(c) },
  ];
  const drilldownData = embarques.map((e, i) => ({
    ...e,
    id: `${e.numEmbarque != null ? e.numEmbarque : "row"}-${i}`,
  }));
  const activeRange = QUICK_RANGES.find((range) => {
    const r = getQuickRangeDates(range.key);
    return r.desde === filtros.desde && r.ate === filtros.ate;
  });
  const periodoLabel =
    filtros.desde && filtros.ate
      ? `${moment(filtros.desde).format("DD/MM/YYYY")} a ${moment(
          filtros.ate
        ).format("DD/MM/YYYY")}`
      : "";

  return (
    <div className="content">
      {/* Hero: container com o fundo característico (espelha Piso ANTT). */}
      <Card
        className="card-everlog-shell"
        style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
      >
        <CardHeader
          style={{
            ...autopilotHeaderStyle,
            borderBottom: "none",
            padding: "1.65rem 1.65rem 1.45rem",
          }}
        >
          <div style={{ ...autopilotEyebrowStyle, marginBottom: "0.6rem" }}>
            GoBID · Indicadores
          </div>
          <h2
            style={{
              ...autopilotTitleStyle,
              margin: "0 0 0.55rem",
              fontSize: "2rem",
              fontWeight: 800,
              lineHeight: 1.15,
            }}
          >
            Savings
          </h2>
          <p
            style={{
              ...autopilotDescriptionStyle,
              maxWidth: 820,
              marginBottom: 0,
              fontSize: "0.98rem",
              lineHeight: 1.65,
            }}
          >
            Economia obtida nas cotações GoBID: compara o baseline com o valor
            contratado por embarque, no embarcador e período selecionados.
          </p>
        </CardHeader>
      </Card>

      {/* Painel de controle: período (campo preferencial) + Filtros + resumo. */}
      <Card
        className="card-everlog-shell"
        style={{
          ...autopilotCardStyle,
          marginTop: "1.25rem",
          background: "var(--everlog-card-hero-bg)",
        }}
      >
        <CardBody style={autopilotBodyStyle}>
          <div className="sav-busca">
            <div className="sav-busca__label">
              <span className="sav-busca__title">Período</span>
              {periodoLabel ? (
                <span className="sav-busca__hint">
                  {periodoLabel}
                  {!activeRange ? " (personalizado)" : ""}
                </span>
              ) : null}
            </div>
            <div className="sav-quick-ranges">
              {QUICK_RANGES.map((range) => (
                <button
                  key={range.key}
                  type="button"
                  className={`sav-quick-range${
                    activeRange && activeRange.key === range.key ? " is-active" : ""
                  }`}
                  disabled={loading}
                  onClick={() => applyQuickRange(range.key)}
                >
                  {range.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              className={`sav-busca__filtros${mostrarFiltros ? " is-open" : ""}`}
              onClick={() => setMostrarFiltros((v) => !v)}
              aria-expanded={mostrarFiltros}
            >
              <i className="tim-icons icon-settings-gear-63" />
              <span>Filtros</span>
            </button>
          </div>

          {mostrarFiltros ? (
            <section className="sav-advfilters">
              <Row className="align-items-end">
                <Col lg="3" md="6">
                  <FormGroup>
                    <Label>De</Label>
                    <Input
                      type="date"
                      value={filtros.desde}
                      disabled={loading}
                      onChange={(e) => {
                        const valor = e.target.value;
                        setFiltros((current) => ({ ...current, desde: valor }));
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col lg="3" md="6">
                  <FormGroup>
                    <Label>Até</Label>
                    <Input
                      type="date"
                      value={filtros.ate}
                      disabled={loading}
                      onChange={(e) => {
                        const valor = e.target.value;
                        setFiltros((current) => ({ ...current, ate: valor }));
                      }}
                    />
                  </FormGroup>
                </Col>
                <Col lg="6" md="12" className="sav-advfilters__actions">
                  <Button color="info" disabled={loading} onClick={() => loadDashboard(filtros)}>
                    {loading ? "Carregando..." : "Consultar"}
                  </Button>
                </Col>
              </Row>
            </section>
          ) : null}

          {hasResults ? (
            <section className="sav-status-section">
              <div className="sav-status-section__head">
                <div className="sav-status-section__title">Resumo do período</div>
                <div className="sav-status-section__subtitle">
                  Economia contratada nas cotações GoBID contra o baseline.
                </div>
              </div>
              <div className="sav-status-grid">
                <StatusCard
                  label="Total economizado"
                  tone="success"
                  iconClassName="tim-icons icon-money-coins"
                  value={fmtBRL(cards.totalEconomizado.valor)}
                  helper="Baseline menos valor contratado no período"
                />
                <StatusCard
                  label="% de economia"
                  tone="info"
                  iconClassName="tim-icons icon-chart-bar-32"
                  value={fmtPct(cards.percentualEconomia.pct)}
                  helper="Economia sobre o baseline do período"
                />
                <StatusCard
                  label="Variação vs período anterior"
                  tone={variacaoTone}
                  iconClassName={
                    variacao != null && variacao < 0
                      ? "tim-icons icon-minimal-down"
                      : "tim-icons icon-minimal-up"
                  }
                  value={fmtPctSigned(variacao)}
                  helper="Economia comparada ao período imediatamente anterior"
                />
                <StatusCard
                  label="Nº de embarques"
                  tone="warning"
                  iconClassName="tim-icons icon-delivery-fast"
                  value={fmtInt(data.totalEmbarques)}
                  helper="Embarques com cotação GoBID no período"
                />
              </div>
            </section>
          ) : null}
        </CardBody>
      </Card>

      {loading ? (
        <div className="sav-loading">
          <ClipLoader color={AUTOPILOT_COLORS.title} size={32} />
        </div>
      ) : error ? (
        <Card
          className="card-everlog-shell"
          style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
        >
          <CardBody style={autopilotBodyStyle}>
            <div className="sav-state sav-state--error">{error}</div>
          </CardBody>
        </Card>
      ) : isEmpty ? (
        <Card
          className="card-everlog-shell"
          style={{ ...autopilotCardStyle, background: "var(--everlog-card-hero-bg)" }}
        >
          <CardBody style={autopilotBodyStyle}>
            <div className="sav-state">
              Nenhum embarque com cotação GoBID no período. Ajuste o período ou
              verifique se o embarcador possui cotações contratadas.
            </div>
          </CardBody>
        </Card>
      ) : hasResults ? (
        <>
          <div className="sav-panels">
            <Card
              className="card-everlog-shell sav-panel"
              style={{
                ...autopilotCardStyle,
                marginTop: "1.25rem",
                background: "var(--everlog-card-hero-bg)",
              }}
            >
              <CardHeader style={autopilotHeaderStyle}>
                <div style={autopilotEyebrowStyle}>Ranking</div>
                <div style={autopilotTitleStyle}>
                  Top transportadoras por economia
                </div>
              </CardHeader>
              <CardBody style={autopilotBodyStyle}>
                {topTransportadoras.length === 0 ? (
                  <div className="sav-state">
                    Nenhuma transportadora com economia no período.
                  </div>
                ) : (
                  <ul className="sav-rank">
                    {topTransportadoras.map((item, index) => {
                      const ativo =
                        transportadoraFiltro === item.transportadora;
                      return (
                        <li
                          key={item.transportadora || index}
                          className={
                            "sav-rank__item sav-rank__item--click" +
                            (ativo ? " is-active" : "")
                          }
                          onClick={() =>
                            toggleTransportadora(item.transportadora)
                          }
                          title={
                            ativo
                              ? "Remover filtro dos detalhes"
                              : "Filtrar os detalhes por esta transportadora"
                          }
                        >
                          <span className="sav-rank__pos">{index + 1}</span>
                          <span className="sav-rank__nome">
                            {item.transportadora || "-"}
                            <span className="sav-rank__qtd">
                              {fmtInt(item.qtd)} embarque(s)
                            </span>
                          </span>
                          <span className="sav-rank__valor">
                            {fmtBRL(item.economia)}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </CardBody>
            </Card>

            <Card
              className="card-everlog-shell sav-panel"
              style={{
                ...autopilotCardStyle,
                marginTop: "1.25rem",
                background: "var(--everlog-card-hero-bg)",
              }}
            >
              <CardHeader style={autopilotHeaderStyle}>
                <div style={autopilotEyebrowStyle}>Tendência</div>
                <div style={autopilotTitleStyle}>Economia por mês</div>
              </CardHeader>
              <CardBody style={autopilotBodyStyle}>
                {tendenciaSerie.length === 0 ? (
                  <div className="sav-state">
                    Sem série mensal disponível para o período.
                  </div>
                ) : (
                  <div className="sav-table-wrap">
                    <Table className="sav-table" responsive>
                      <thead>
                        <tr>
                          <th>Mês</th>
                          <th className="sav-num">Economia</th>
                          <th className="sav-num">%</th>
                        </tr>
                      </thead>
                      <tbody>
                        {tendenciaSerie.map((ponto) => (
                          <tr key={ponto.mes}>
                            <td>{fmtMes(ponto.mes)}</td>
                            <td className="sav-num">{fmtBRL(ponto.economia)}</td>
                            <td className="sav-num">{fmtPct(ponto.percentual)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>
          </div>

          <Card
            className="card-everlog-shell"
            style={{
              ...autopilotCardStyle,
              marginTop: "1.25rem",
              background: "var(--everlog-card-hero-bg)",
            }}
          >
            <CardHeader style={autopilotHeaderStyle}>
              <div className="sav-drill-head">
                <div>
                  <div style={autopilotEyebrowStyle}>Detalhe</div>
                  <div style={autopilotTitleStyle}>Embarques do período</div>
                </div>
                <Button
                  color="success"
                  size="sm"
                  disabled={exportLoading || embarques.length === 0}
                  onClick={handleExport}
                >
                  <i className="tim-icons icon-cloud-download-93" />{" "}
                  {exportLoading ? "Gerando..." : "Exportar XLSX"}
                </Button>
              </div>
            </CardHeader>
            <CardBody style={autopilotBodyStyle}>
              {transportadoraFiltro ? (
                <div className="sav-drill-filter">
                  <span>
                    Detalhes filtrados por{" "}
                    <strong>{transportadoraFiltro}</strong>
                  </span>
                  <button
                    type="button"
                    className="sav-drill-filter__clear"
                    onClick={() => toggleTransportadora(transportadoraFiltro)}
                  >
                    limpar filtro ✕
                  </button>
                </div>
              ) : null}
              <CustomTable
                data={drilldownData}
                columns={drilldownColumns}
                loading={drilldownLoading}
                sizePerPage={10}
                showToolbar={false}
                themedPagination
                tableClasses="text-left"
              />
              {embarques.length >= 2000 ? (
                <p className="sav-footnote">
                  Mostrando os primeiros 2000 embarques. Use Exportar XLSX para a
                  lista completa.
                </p>
              ) : null}
            </CardBody>
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default SavingsGoBID;
